// src/pages/KissCamTermsPage.jsx
import React from 'react';
import useLegalLanguage from '../hooks/useLegalLanguage';
import styled from 'styled-components';
import { PageTitle, LanguageToggle, LangButton, ContactBlock } from '../components/ui/PageElements';
import PageMeta from '../components/PageMeta';

const LegalWrapper = styled.div`
    max-width: 800px;
    margin: 50px auto 100px;
    padding: 0 20px;
    color: var(--text-color);
    line-height: 1.7;

    h2 {
        font-size: 24px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 1px;
        margin-top: 40px;
        margin-bottom: 20px;
        color: var(--primary-color);
        position: relative;
        &:before {
            content: "§";
            color: var(--secondary-color);
            margin-right: 10px;
            opacity: 0.7;
        }
    }

    p, li {
        font-size: 16px;
        color: #cccccc;
        margin-bottom: 15px;
    }

    ul {
        list-style: disc;
        padding-left: 30px;
        margin-bottom: 20px;
    }

    a {
        color: var(--secondary-color);
        text-decoration: underline;
        text-underline-offset: 3px;
        &:hover {
            color: var(--primary-color);
        }
    }

    strong {
        color: var(--text-color);
        font-weight: 700;
    }
`;

const LastUpdatedText = styled.p`
    font-style: italic;
    color: var(--text-muted);
    margin-bottom: 30px !important;
    font-size: 14px !important;
`;

const CURRENT_DATE = new Date().toLocaleDateString('en-CA');

const content = {
    en: {
        title: "KissCam Terms of Use & EULA",
        lastUpdated: "Last Updated",
        intro: `These Terms of Use ("Terms") govern your download and use of the KissCam screen capture application ("Software")
            provided by Lucas Perron ("LPERRON_DEV", "I", "me"). By downloading, installing, or using the Software,
            you agree to be bound by these Terms.`,

        // Section 1: License
        section1Title: "1. Software License (EULA)",
        section1GrantTitle: "Grant of License:",
        section1GrantText: "I grant you a limited, non-exclusive, non-transferable, revocable license to download, install, and use the Software on your own macOS devices for personal or professional use, free of charge.",
        section1RestrictionsTitle: "Restrictions:",
        section1RestrictionsIntro: "You may NOT:",
        section1Restrictions: [
            "Redistribute, sell, rent, lease, sublicense, or otherwise transfer the Software or its installer to any third party.",
            "Reverse engineer, decompile, or disassemble the Software, except to the extent that such activity is expressly permitted by applicable law.",
            "Modify or create derivative works based on the Software.",
            "Remove any copyright or proprietary notices from the Software.",
            "Use the Software for any unlawful purpose or in violation of any applicable laws."
        ],

        // Section 2: Closed Source
        section2Title: "2. Closed Source Software",
        section2Text: "The Software is currently distributed as closed source. The source code is not publicly available. This may change in the future, at which point these Terms will be updated accordingly.",

        // Section 3: Intellectual Property
        section3Title: "3. Intellectual Property",
        section3Text: "The Software, including its code, design, user interface, icons, and branding, is the exclusive intellectual property of Lucas Perron and is protected by copyright and other intellectual property laws. Your license does not grant you any ownership rights in the Software.",

        // Section 4: System Permissions
        section4Title: "4. System Permissions",
        section4Text: "The Software requires certain macOS permissions to function properly, including Screen Recording and Accessibility permissions. These permissions are used solely for the Software's core functionality (capturing screenshots and recordings). The Software operates entirely offline: it does not connect to any server, does not collect any data, and does not send anything anywhere. Everything stays on your computer.",

        // Section 5: Disclaimer
        section5Title: "5. Disclaimer of Warranties",
        section5Text: "THE SOFTWARE IS PROVIDED \"AS IS,\" WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NONINFRINGEMENT. IN NO EVENT SHALL LUCAS PERRON BE LIABLE FOR ANY CLAIM, DAMAGES, OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT, OR OTHERWISE, ARISING FROM, OUT OF, OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.",

        // Section 6: Liability
        section6Title: "6. Limitation of Liability",
        section6Text: "TO THE MAXIMUM EXTENT PERMITTED BY LAW, I SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE THE SOFTWARE.",

        // Section 7: Termination
        section7Title: "7. Termination",
        section7Text: "This license is effective until terminated. Your rights under this license will terminate automatically without notice if you fail to comply with any of its terms. Upon termination, you must cease all use of the Software and destroy all copies in your possession.",

        // Section 8: Changes
        section8Title: "8. Changes to These Terms",
        section8Text: "I reserve the right to modify these Terms at any time. I will notify you of any changes by posting the new Terms on this site and updating the \"Last Updated\" date. Your continued use of the Software after any such changes constitutes your acceptance of the new Terms.",

        // Section 9: Contact
        section9Title: "9. Contact Information",
        section9Intro: "If you have any questions about these Terms, please contact me:"
    },

    fr: {
        title: "KissCam Conditions d'Utilisation & EULA",
        lastUpdated: "Dernière mise à jour",
        intro: `Les présentes Conditions d'Utilisation ("Conditions") régissent votre téléchargement et utilisation de l'application de capture d'écran KissCam ("Logiciel")
            fournie par Lucas Perron ("LPERRON_DEV", "je", "moi"). En téléchargeant, installant ou utilisant le Logiciel,
            vous acceptez d'être lié par ces Conditions.`,

        // Section 1: License
        section1Title: "1. Licence du Logiciel (EULA)",
        section1GrantTitle: "Octroi de licence :",
        section1GrantText: "Je vous accorde une licence limitée, non exclusive, non transférable et révocable pour télécharger, installer et utiliser le Logiciel sur vos propres appareils macOS pour un usage personnel ou professionnel, gratuitement.",
        section1RestrictionsTitle: "Restrictions :",
        section1RestrictionsIntro: "Vous ne pouvez PAS :",
        section1Restrictions: [
            "Redistribuer, vendre, louer, sous-licencier ou transférer le Logiciel ou son installateur à un tiers.",
            "Désassembler, décompiler ou rétro-concevoir le Logiciel, sauf dans la mesure où cela est expressément autorisé par la loi applicable.",
            "Modifier ou créer des oeuvres dérivées basées sur le Logiciel.",
            "Supprimer les mentions de droits d'auteur ou de propriété du Logiciel.",
            "Utiliser le Logiciel à des fins illégales ou en violation des lois applicables."
        ],

        // Section 2: Closed Source
        section2Title: "2. Logiciel à Code Fermé",
        section2Text: "Le Logiciel est actuellement distribué en code fermé. Le code source n'est pas disponible publiquement. Cela pourrait changer à l'avenir, auquel cas ces Conditions seront mises à jour en conséquence.",

        // Section 3: Intellectual Property
        section3Title: "3. Propriété Intellectuelle",
        section3Text: "Le Logiciel, y compris son code, design, interface utilisateur, icônes et image de marque, est la propriété intellectuelle exclusive de Lucas Perron et est protégé par le droit d'auteur et autres lois sur la propriété intellectuelle. Votre licence ne vous confère aucun droit de propriété sur le Logiciel.",

        // Section 4: System Permissions
        section4Title: "4. Permissions Système",
        section4Text: "Le Logiciel nécessite certaines permissions macOS pour fonctionner correctement, notamment les permissions d'Enregistrement d'écran et d'Accessibilité. Ces permissions sont utilisées uniquement pour la fonctionnalité principale du Logiciel (capture d'écran et enregistrements). Le Logiciel fonctionne entièrement hors ligne : il ne se connecte à aucun serveur, ne collecte aucune donnée et n'envoie rien nulle part. Tout reste sur votre ordinateur.",

        // Section 5: Disclaimer
        section5Title: "5. Exclusion de Garanties",
        section5Text: "LE LOGICIEL EST FOURNI \"EN L'ÉTAT\", SANS GARANTIE D'AUCUNE SORTE, EXPRESSE OU IMPLICITE, Y COMPRIS MAIS SANS S'Y LIMITER LES GARANTIES DE QUALITÉ MARCHANDE, D'ADÉQUATION À UN USAGE PARTICULIER ET D'ABSENCE DE CONTREFAÇON. EN AUCUN CAS LUCAS PERRON NE SERA RESPONSABLE DE TOUTE RÉCLAMATION, DOMMAGE OU AUTRE RESPONSABILITÉ, QUE CE SOIT DANS LE CADRE D'UN CONTRAT, D'UN DÉLIT OU AUTREMENT, DÉCOULANT DE OU EN RELATION AVEC LE LOGICIEL OU L'UTILISATION OU AUTRES OPÉRATIONS AVEC LE LOGICIEL.",

        // Section 6: Liability
        section6Title: "6. Limitation de Responsabilité",
        section6Text: "DANS LES LIMITES AUTORISÉES PAR LA LOI, JE NE SERAI PAS RESPONSABLE DES DOMMAGES INDIRECTS, ACCESSOIRES, SPÉCIAUX, CONSÉCUTIFS OU PUNITIFS, NI DE TOUTE PERTE DE PROFITS OU DE REVENUS, DIRECTS OU INDIRECTS, NI DE TOUTE PERTE DE DONNÉES, D'UTILISATION, DE CLIENTÈLE OU D'AUTRES PERTES INTANGIBLES RÉSULTANT DE VOTRE ACCÈS OU UTILISATION OU IMPOSSIBILITÉ D'ACCÉDER OU D'UTILISER LE LOGICIEL.",

        // Section 7: Termination
        section7Title: "7. Résiliation",
        section7Text: "Cette licence est effective jusqu'à sa résiliation. Vos droits en vertu de cette licence seront automatiquement résiliés sans préavis si vous ne respectez pas l'une de ses conditions. En cas de résiliation, vous devez cesser toute utilisation du Logiciel et détruire toutes les copies en votre possession.",

        // Section 8: Changes
        section8Title: "8. Modifications des Conditions",
        section8Text: "Je me réserve le droit de modifier ces Conditions à tout moment. Je vous informerai de tout changement en publiant les nouvelles Conditions sur ce site et en mettant à jour la date \"Dernière mise à jour\". Votre utilisation continue du Logiciel après de telles modifications constitue votre acceptation des nouvelles Conditions.",

        // Section 9: Contact
        section9Title: "9. Coordonnées",
        section9Intro: "Si vous avez des questions concernant ces Conditions, veuillez me contacter :"
    }
};

const KissCamTermsPage = () => {
    const [lang, setLang] = useLegalLanguage();
    const t = content[lang];

    return (
        <LegalWrapper>
            <PageMeta
                title="KissCam Terms of Use"
                description="KissCam Terms of Use and EULA for the KissCam screen capture application for macOS."
                canonicalPath="/kisscam-terms-of-use"
            />
            <LanguageToggle>
                <LangButton $active={lang === 'en'} onClick={() => setLang('en')}>EN</LangButton>
                <LangButton $active={lang === 'fr'} onClick={() => setLang('fr')}>FR</LangButton>
            </LanguageToggle>
            <PageTitle style={{ textAlign: 'left', fontSize: '36px', marginBottom: '10px' }}>{t.title}</PageTitle>
            <LastUpdatedText>{t.lastUpdated}: {CURRENT_DATE}</LastUpdatedText>

            <p>{t.intro}</p>

            {/* Section 1: License */}
            <h2>{t.section1Title}</h2>
            <p><strong>{t.section1GrantTitle}</strong> {t.section1GrantText}</p>
            <p><strong>{t.section1RestrictionsTitle}</strong> {t.section1RestrictionsIntro}</p>
            <ul>
                {t.section1Restrictions.map((item, i) => <li key={i}>{item}</li>)}
            </ul>

            {/* Section 2: Closed Source */}
            <h2>{t.section2Title}</h2>
            <p>{t.section2Text}</p>

            {/* Section 3: Intellectual Property */}
            <h2>{t.section3Title}</h2>
            <p>{t.section3Text}</p>

            {/* Section 4: System Permissions */}
            <h2>{t.section4Title}</h2>
            <p>{t.section4Text}</p>

            {/* Section 5: Disclaimer */}
            <h2>{t.section5Title}</h2>
            <p>{t.section5Text}</p>

            {/* Section 6: Liability */}
            <h2>{t.section6Title}</h2>
            <p>{t.section6Text}</p>

            {/* Section 7: Termination */}
            <h2>{t.section7Title}</h2>
            <p>{t.section7Text}</p>

            {/* Section 8: Changes */}
            <h2>{t.section8Title}</h2>
            <p>{t.section8Text}</p>

            {/* Section 9: Contact */}
            <h2>{t.section9Title}</h2>
            <p>{t.section9Intro}</p>
            <ContactBlock lang={lang} />
        </LegalWrapper>
    );
};

export default KissCamTermsPage;
